import { useState, useEffect } from "react";

// Helper to get device name based on window width
function getDeviceName() {
  const width = window.innerWidth;
  if (width <= 768) return "Mobile";
  if (width <= 1024) return "Tablet";
  return "Desktop";
}

// Helper to get IP address from public API
async function fetchDeviceIPOnline() {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to fetch IP:", error);
    return "Unavailable";
  }
}

// Helper to attempt finding local IP using WebRTC
async function fetchDeviceIPLocally() {
  try {
    // Use proper TypeScript handling for vendor prefixes
    const RTCPeerConnection = window.RTCPeerConnection;
    
    // Check if WebRTC is supported
    if (!RTCPeerConnection) {
      console.warn("WebRTC is not supported by this browser.");
      return "Unavailable";
    }
    
    const pc = new RTCPeerConnection();
    const localIPs = new Set();
    
    pc.createDataChannel("");
    
    await pc.createOffer()
      .then(offer => pc.setLocalDescription(offer))
      .catch(error => console.error("Failed to create offer:", error));
    
    return new Promise((resolve) => {
      pc.onicecandidate = (event) => {
        if (!event || !event.candidate) {
          pc.close();
          resolve(localIPs.size > 0 ? Array.from(localIPs).join(", ") : "Unavailable");
          return;
        }
        
        const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
        const ipMatch = ipRegex.exec(event.candidate.candidate);
        if (ipMatch) {
          localIPs.add(ipMatch[1]);
        }
      };
    });
  } catch (error) {
    console.error("Failed to fetch local IP:", error);
    return "Unavailable";
  }
}

// Interface for device information
interface DeviceInfo {
  userAgent: string;
  deviceName: string;
  deviceIP: string;
  deviceMacAddress: string;
}

// Main hook
export function useDeviceInfo() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    userAgent: "",
    deviceName: "",
    deviceIP: "Loading...",
    deviceMacAddress: "Unavailable", // Cannot fetch MAC in browser
  });

  useEffect(() => {
    async function getInfo() {
      // Try online IP first, fall back to local detection
      let ip = await fetchDeviceIPOnline();
      if (ip === "Unavailable") {
        ip = await fetchDeviceIPLocally();
      }

      setDeviceInfo({
        userAgent: navigator.userAgent,
        deviceName: getDeviceName(),
        deviceIP: ip,
        deviceMacAddress: "Unavailable",
      });
    }
    
    getInfo();
  }, []);

  return deviceInfo;
}